import React, { useState } from "react";
import { MapPin, Camera, FileCheck2, CheckCircle2, XCircle, UserCheck, Bell, FileSignature, Send, ShieldCheck, AlertTriangle, MinusCircle } from "lucide-react";
import { FileImage } from "@/components/FileImage";
import { formatDistance } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const ICONS = {
  order_created: Send,
  assigned: UserCheck,
  accepted: UserCheck,
  declined: XCircle,
  affidavit_completed: FileSignature,
  approved: CheckCircle2,
  rejected: XCircle,
  notified: Bell,
};

function fmt(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

export function AuditTrail({ order }) {
  const events = order.audit_log || [];
  return (
    <div data-testid="audit-trail" className="relative pl-6">
      <div className="absolute left-[9px] top-1 bottom-1 w-px bg-slate-200" />
      {events.map((e, i) => {
        const isAttempt = e.event?.startsWith("attempt_");
        const Icon = isAttempt ? MapPin : ICONS[e.event] || FileCheck2;
        const served = e.detail?.toLowerCase().includes("served") && !e.detail?.toLowerCase().includes("not served");
        return (
          <div key={i} className="relative mb-5 fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <span className={`absolute -left-6 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${
              e.event === "approved" || served ? "bg-emerald-500" :
              e.event === "rejected" || e.event === "declined" ? "bg-red-500" : "bg-slate-800"
            }`}>
              <Icon className="h-3 w-3 text-white" />
            </span>
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">{e.detail}</p>
                <span className="whitespace-nowrap font-mono-arps text-[11px] text-slate-400">{fmt(e.at)}</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">by {e.by}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AttemptCard({ attempt }) {
  const gps = attempt.gps || {};
  const served = attempt.outcome === "served";
  return (
    <div data-testid={`attempt-card-${attempt.attempt_number}`} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-base font-bold text-slate-900">Attempt #{attempt.attempt_number}</h4>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
          served ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
        }`}>
          {served ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {attempt.outcome.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
        </span>
      </div>
      <p className="mt-1 font-mono-arps text-xs text-slate-400">{fmt(attempt.timestamp)}</p>

      {gps.lat != null && (
        <p data-testid={`attempt-captured-address-${attempt.attempt_number}`} className="mt-1 text-xs text-slate-600">
          <span className="font-semibold text-slate-500">Captured Location:</span> {gps.address || "Captured location address unavailable"}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {gps.lat != null ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono-arps text-[11px] text-slate-600">
            <MapPin className="h-3.5 w-3.5 text-emerald-600" />
            {Number(gps.lat).toFixed(4)}, {Number(gps.lng).toFixed(4)} {gps.accuracy ? `±${Math.round(gps.accuracy)}m` : ""}
          </span>
        ) : (
          <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-400">No GPS</span>
        )}
        <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
          <Camera className="h-3.5 w-3.5 text-sky-600" /> {(attempt.photos || []).length} photo(s)
        </span>
        {attempt.location_confirmation && (
          <span data-testid={`attempt-loc-confirm-${attempt.attempt_number}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
            Location Confirmation: <span className="font-semibold text-slate-800">{String(attempt.location_confirmation).toUpperCase()}</span>
          </span>
        )}
        {gps.validation?.status && (
          <span data-testid={`attempt-proximity-${attempt.attempt_number}`} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold ${
            gps.validation.status === "pass" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
            gps.validation.status === "flagged" ? "border-red-200 bg-red-50 text-red-700" :
            "border-slate-200 bg-slate-50 text-slate-500"}`}>
            {gps.validation.status === "pass" ? <ShieldCheck className="h-3.5 w-3.5" /> : gps.validation.status === "flagged" ? <AlertTriangle className="h-3.5 w-3.5" /> : <MinusCircle className="h-3.5 w-3.5" />}
            {gps.validation.status === "pass" ? "Location verified" : gps.validation.status === "flagged" ? `Outside service location${gps.validation.distance_m != null ? ` · ${formatDistance(gps.validation.distance_m)}` : ""}` : "Proximity not validated"}
          </span>
        )}
      </div>

      {(attempt.photos || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {attempt.photos.map((pid) => (
            <Dialog key={pid}>
              <DialogTrigger asChild>
                <button data-testid={`attempt-photo-${pid}`} className="overflow-hidden rounded-md border border-slate-200 transition hover:ring-2 hover:ring-emerald-400">
                  <FileImage fileId={pid} className="h-16 w-16 object-cover" alt="Attempt evidence" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Photo Evidence</DialogTitle></DialogHeader>
                <FileImage fileId={pid} className="w-full rounded-lg" alt="Attempt evidence full" />
              </DialogContent>
            </Dialog>
          ))}
        </div>
      )}

      {attempt.notes && <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{attempt.notes}</p>}
    </div>
  );
}
